/**
 * 旋转草图 dirty 预览调度器。
 *
 * 文件定位：
 * 1. 该工具位于 web-client/composables/revolve，服务旋转凸台/旋转切除会话；
 * 2. 它只负责合并高频草图 dirty 事件，不接触 Viewer、cad-core 或 Vue 响应式状态；
 * 3. 真正的轮廓重建与预览刷新仍由 `useRevolveFeature` 统一执行。
 */

/**
 * 旋转 dirty 预览调度器。
 */
export interface RevolveDirtyPreviewScheduler {
  /** 调度指定草图的 dirty 刷新。 */
  schedule: (sketchId: string) => void;
  /** 清理待执行 dirty 刷新。 */
  clear: () => void;
}

/**
 * 创建旋转 dirty 预览调度器。
 *
 * 设计说明：
 * 1. 草图拖拽会持续产生 dirty 事件，不能每次都立即请求 worker 预览；
 * 2. 这里用尾随 debounce 只保留最新一批草图 ID，避免堵塞正式特征重建的 worker 请求；
 * 3. flush 收到的是去重后的草图 ID 列表，保持调度器自身不理解业务语义。
 *
 * @param input 调度器配置。
 * @returns 调度器实例。
 */
export function createRevolveDirtyPreviewScheduler(input: {
  /** 合并延迟。 */
  delayMs: number;
  /** 执行合并后的 dirty 刷新。 */
  flush: (sketchIds: string[]) => void;
}): RevolveDirtyPreviewScheduler {
  const pendingSketchIds = new Set<string>();
  let timer: number | null = null;

  /**
   * 清理待执行刷新。
   */
  function clear(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    pendingSketchIds.clear();
  }

  /**
   * 调度 dirty 刷新。
   *
   * @param sketchId 草图 ID。
   */
  function schedule(sketchId: string): void {
    pendingSketchIds.add(sketchId);
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      timer = null;
      const sketchIds = Array.from(pendingSketchIds);
      pendingSketchIds.clear();
      input.flush(sketchIds);
    }, input.delayMs);
  }

  return {
    schedule,
    clear,
  };
}
